import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useConfigService } from '@thd-nucleus/experience-context';
import {
  FormController,
  Radio,
  RadioGroup,
  SelectionControlLabel,
  DrawerHeader,
  DrawerBody,
  Typography,
  Alert
} from '@one-thd/sui-atomic-components';
import { AddToProductDetails } from '../AddToProductDetails/AddToProductDetails';
import { TOO_MANY_PRODUCTS_MESSAGE, MAX_QUOTE_ITEMS } from '../../AddToConstants';

export const AddToSelection = ({
  products, addToType, setAddToType, handleClose, isProjectsEligible, copyProjectData,
}) => {
  const showAddToQuoteButton = useConfigService('fs:isATQEnabled');

  const handleChange = (event) => {
    const { value } = event.target;
    if (value === 'Project' && copyProjectData?.length) {
      setAddToType('CopyProject');
    } else {
      setAddToType(value);
    }
  };

  const projectSelectionWrapperClass = classNames(
    'sui-py-4',
    {
      'sui-hidden': !isProjectsEligible,
      'sui-border-b-1': showAddToQuoteButton,
      'sui-border-primary': showAddToQuoteButton,
      'sui-border-solid': showAddToQuoteButton
    }
  );

  return (
    <>
      <DrawerHeader
        title="Add to..."
        onClose={handleClose}
      />
      <DrawerBody>
        {products?.length <= 1 && (
          <AddToProductDetails products={products} />
        )}
        <div className="sui-min-h-screen">
          <FormController>
            <RadioGroup
              aria-labelledby="add-to-buttons-group-label"
              value={addToType}
              name="add-to-radio-buttons-group"
              onChange={handleChange}
            >
              <div
                className="sui-border-b-1 sui-border-primary sui-border-solid sui-py-4"
              >
                <SelectionControlLabel value="List" label="List">
                  <Radio />
                </SelectionControlLabel>
                <div>
                  <Typography>
                    Your reusable catalog of essentials. Keep a curated set of products you use often organized
                    by job, task, or material type.
                  </Typography>
                </div>
              </div>
              <div
                className={projectSelectionWrapperClass}
                data-testid="project-radio-button"
              >
                <SelectionControlLabel value="Project" label="Project">
                  <Radio />
                </SelectionControlLabel>
                <div className="sui-py-4">
                  <Typography>
                    Your central hub for each job. Organize and track everything from materials to progress.
                    Pull in items from your Lists, view connected Quotes, and stay on top of orders
                    all in one place.
                  </Typography>
                </div>
              </div>
            </RadioGroup>
          </FormController>
          { (showAddToQuoteButton) && (
            <FormController>
              <RadioGroup
                aria-labelledby="add-to-buttons-group-label-2"
                value={addToType}
                name="add-to-radio-buttons-group-2"
                onChange={handleChange}
              >
                {products?.length > MAX_QUOTE_ITEMS && (
                  <div className="sui-pt-4">
                    <Alert
                      data-testid="products-selection-alert"
                      status="error"
                    >
                      {TOO_MANY_PRODUCTS_MESSAGE}
                    </Alert>
                  </div>
                )}
                <div
                  className="sui-py-4"
                >
                  <SelectionControlLabel disabled={products?.length > MAX_QUOTE_ITEMS} value="Quote" label="Quote">
                    <Radio />
                  </SelectionControlLabel>
                  <div>
                    <Typography
                      color={products?.length > MAX_QUOTE_ITEMS ? 'inactive' : 'primary'}
                    >
                      Lock in pricing before you buy. Create a Quote to confirm costs and secure the prices for
                      materials you plan to order.
                    </Typography>
                  </div>
                </div>
              </RadioGroup>
            </FormController>
          )}
        </div>
      </DrawerBody>
    </>
  );
};

AddToSelection.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number
  })),
  copyProjectData: PropTypes.arrayOf(PropTypes.shape({})),
  addToType: PropTypes.string,
  setAddToType: PropTypes.func,
  handleClose: PropTypes.func,
  isProjectsEligible: PropTypes.bool
};

AddToSelection.displayName = 'AddToSelection';

AddToSelection.defaultProps = {
  products: [],
  copyProjectData: [],
  addToType: null,
  setAddToType: () => {
  },
  handleClose: () => {
  },
  isProjectsEligible: false
};
